<template>
  <div class="p-textarea-wrap">
    <label class="p-textarea" :class="{ error: entryError }">
      <textarea
        ref="textareaRef"
        :name="name"
        class="custom-textarea"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        :maxlength="maxLength"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="(e) => $emit('enter', e)"
        @input="onInput"
      />
      <div class="slot-icon">
        <slot name="right" />
      </div>
    </label>
    <transition name="input-error">
      <div v-if="entryError" class="error-message">
        {{ entryError }}
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { useDebounceFn } from '@vueuse/core';

export default defineComponent({
  name: 'PTextarea',
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: String,
      default: '255',
    },
  },
  emits: ['update:value', 'focus', 'blur', 'input', 'validation-failed'],
  setup(props, { emit }) {
    const textareaRef = ref<HTMLElement | null>(null);

    const focused = ref<boolean>(false);
    const entryError = computed(() => {
      if (props.error && !focused.value) {
        emit('validation-failed');
        return props.error;
      }

      return null;
    });

    onMounted(() => {
      window.addEventListener('resize', onResize);

      resize();
    });

    const resize = () => {
      if (props.autoHeight && textareaRef.value) {
        const scrollHeight = textareaRef.value?.scrollHeight;
        nextTick(() => {
          if (scrollHeight && textareaRef.value) {
            textareaRef.value.style.minHeight = '0px';
            textareaRef.value.style.minHeight =
              textareaRef.value?.scrollHeight + 'px';
          }
        });
      }
    };

    const onResize = useDebounceFn(() => {
      resize();
    }, 200);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize);
    });

    function onFocus(e: Event): void {
      focused.value = true;
      emit('focus', e);
    }

    function onBlur(e: Event): void {
      focused.value = false;
      emit('blur', e);
    }

    function onInput(e: Event): void {
      resize();
      const target = e.target as HTMLTextAreaElement;
      const maxText = target.value;
      const maxString =
        props.maxLength && +props.maxLength ? +props.maxLength : -1;
      emit('input', maxText.substring(0, maxString));
      emit('update:value', maxText.substring(0, maxString));

      // android problem with maxLength and shadowdom
      nextTick(() => {
        if (textareaRef.value && props.value) {
          // @ts-ignore
          textareaRef.value.value = props.value.substring(0, maxString);
        }
      });
    }

    return {
      focused,
      textareaRef,
      entryError,
      onFocus,
      onInput,
      onBlur,
    };
  },
});
</script>
<style scoped lang="scss" src="./p-textarea-scss.scss" />
